import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  collection,
  collectionGroup,
  doc,
  documentId,
  getDocs,
  onSnapshot,
  query,
  orderBy,
  limit,
  where,
} from "firebase/firestore";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { dbFinancialPromotions } from "../../../../backend/firebaseconfig";
import { useUserEmail } from "../../../../context/UserEmailContext";
import { useUserRole } from "../../../../context/UserRoleContext";
import Sidebar from "../../../DesignSystem/Sidebar";
import Breadcrumb from "../../../DesignSystem/Breadcrumb/Breadcrumb";
import "../../../../css/FinancialPromotions/SubmissionDetails/FPDetails.css";
import "../../../../css/FinancialPromotions/FinancialPromotionsSubmission.css";
import { ReactComponent as BackIcon } from "../../../../assets/icons/financialPromotions/Back.svg";
import { ReactComponent as CheckCircleIcon } from "../../../../assets/icons/aml/checkCircleFilled.svg";
import { ReactComponent as CopyIcon } from "../../../../assets/icons/aml/Copy.svg";
import StatusTag from "../components/buttons/StatusTag";
import getDecision from "../dashboard/helperFunctions/GetDecision";
import { formatDate } from "../dashboard/utils/DashboardUtils";
import { looksLikeUrl, MAX_FILES, MAX_TEXT_CHARS } from "../utils/fpUtils";
import { submitFpForReview } from "../utils/fpRequest";
import FileUpload from "../components/uploads/FileUpload";
import FPPrimaryButton from "../components/buttons/PrimaryButton";

import {
  getStorage,
  ref as storageRef,
  getDownloadURL,
} from "firebase/storage";

const BACKEND_URL =
  process.env.REACT_APP_FINANCIAL_PROMOTIONS_API_URL ||
  "https://financialpromotionsrifa.onrender.com";

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20 MB

const formatDateTime = (value) => {
  const date = value?.toDate ? value.toDate() : value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const toTimestampMs = (value) => {
  if (!value) return 0;
  if (typeof value?.toDate === "function") {
    const d = value.toDate();
    return Number.isNaN(d?.getTime?.()) ? 0 : d.getTime();
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? 0 : value.getTime();
  }
  if (typeof value?.seconds === "number") {
    const ms =
      value.seconds * 1000 + Math.floor((value.nanoseconds || 0) / 1e6);
    return Number.isFinite(ms) ? ms : 0;
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
};

const getAttemptSortTime = (attempt) => {
  return Math.max(
    toTimestampMs(attempt?.updatedAt),
    toTimestampMs(attempt?.completedAt),
    toTimestampMs(attempt?.createdAt),
    toTimestampMs(attempt?.review?.reviewedAt),
  );
};

const isApprovedLike = (value) =>
  ["approved", "complete", "completed"].includes(
    String(value || "")
      .trim()
      .toLowerCase(),
  );

const normalizeName = (value = "") => String(value).trim().toLowerCase();

const normalizeItemKey = (value = "") => String(value).trim().toLowerCase();

const getAttachmentDecisionKey = (attachment) =>
  String(
    attachment?.storagePath ||
      attachment?.downloadURL ||
      attachment?.name ||
      "attachment",
  )
    .trim()
    .toLowerCase();

const toDisplayDecision = (value) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  if (["approved", "complete", "completed"].includes(normalized)) {
    return "Approved";
  }
  if (
    [
      "declined",
      "rejected",
      "failed",
      "request_changes",
      "requested_changes",
    ].includes(normalized)
  ) {
    return "Declined";
  }
  return "Pending";
};

const buildDecisionByFile = (attempt) => {
  const reviewItems = Array.isArray(attempt?.review?.items)
    ? attempt.review.items
    : [];

  const decisionByFile = new Map();
  for (const item of reviewItems) {
    const itemDecision = toDisplayDecision(
      item?.decision || item?.status || item?.result,
    );
    const itemKeys = [
      normalizeItemKey(item?.itemKey),
      normalizeName(item?.fileName),
      normalizeName(item?.name),
      normalizeName(item?.filename),
    ].filter(Boolean);

    for (const key of itemKeys) {
      if (!decisionByFile.has(key) || decisionByFile.get(key) === "Pending") {
        decisionByFile.set(key, itemDecision);
      }
    }
  }

  return decisionByFile;
};

const buildAttemptAttachments = (attempt) => {
  const media = Array.isArray(attempt?.media) ? attempt.media : [];
  const filesMeta = Array.isArray(attempt?.filesMeta) ? attempt.filesMeta : [];
  const list = [];

  for (const m of media) {
    const name = m.originalName || m.fileName || m.name;
    if (!name) continue;
    list.push({
      itemKey: m.itemKey || "",
      name,
      mimeType: m.mimeType || m.type || "",
      size: typeof m.size === "number" ? m.size : null,
      downloadURL: m.downloadURL || null,
      storagePath: m.storagePath || null,
    });
  }

  for (const f of filesMeta) {
    const name = f.originalName || f.fileName || f.name;
    if (!name) continue;
    list.push({
      itemKey: f.itemKey || "",
      name,
      mimeType: f.mimeType || f.type || "",
      size: typeof f.size === "number" ? f.size : null,
      downloadURL: f.downloadURL || null,
      storagePath: f.storagePath || null,
    });
  }

  const byName = new Map();
  for (const item of list) {
    const key = normalizeName(item.name);
    const existing = byName.get(key);
    if (!existing) {
      byName.set(key, item);
      continue;
    }

    const existingScore = Number(
      Boolean(existing.downloadURL || existing.storagePath),
    );
    const nextScore = Number(Boolean(item.downloadURL || item.storagePath));

    if (nextScore > existingScore) {
      byName.set(key, item);
    }
  }

  return Array.from(byName.values());
};

const addOneYear = (value) => {
  const baseDate = value?.toDate
    ? value.toDate()
    : value
      ? new Date(value)
      : null;

  if (!baseDate || Number.isNaN(baseDate.getTime())) return null;
  const next = new Date(baseDate);
  next.setFullYear(next.getFullYear() + 1);
  return next;
};

const formatMultilineText = (value = "") =>
  String(value || "")
    .replace(/\\r\\n/g, "\n")
    .replace(/\\n/g, "\n")
    .trim();

function FPDetails() {
  const { submissionId: routeSubmissionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { userEmail } = useUserEmail();
  const { userRole } = useUserRole();

  const storage = getStorage(); // uses default initialized firebase app

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [resubmitText, setResubmitText] = useState("");
  const [resubmitUrl, setResubmitUrl] = useState("");
  const [resubmitFiles, setResubmitFiles] = useState([]);
  const [resubmitMessage, setResubmitMessage] = useState("");
  const [resubmitLoading, setResubmitLoading] = useState(false);
  const [resubmitError, setResubmitError] = useState("");
  const [resubmitSuccess, setResubmitSuccess] = useState("");

  const submissionId = useMemo(
    () => routeSubmissionId || location.state?.submissionId || "",
    [routeSubmissionId, location.state],
  );

  const normalizedUserRole = String(userRole || "")
    .trim()
    .toLowerCase();
  const isAdminView = ["administrators", "staff"].includes(normalizedUserRole);
  const ownerEmailFromRoute = useMemo(
    () => String(location.state?.ownerEmail || "").trim(),
    [location.state],
  );

  const downloadWithFileName = useCallback(async (url, fileName) => {
    if (!url) return;

    const targetName = fileName || "attachment";

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Download failed with status ${response.status}`);
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = targetName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (downloadError) {
      console.error("Download with filename failed:", downloadError);
      window.open(url, "_blank", "noopener,noreferrer");
    }
  }, []);

  const openFileFromPath = useCallback(
    async (storagePath, fileName) => {
      if (!storagePath) return;
      try {
        const fileRef = storageRef(storage, storagePath);
        const url = await getDownloadURL(fileRef);
        await downloadWithFileName(url, fileName);
      } catch (e) {
        console.error("Failed to open file:", e);
        alert(
          "Could not open this file. Check Storage rules/permissions and path.",
        );
      }
    },
    [downloadWithFileName, storage],
  );

  const openAttachment = useCallback(
    async (att) => {
      if (!att) return;
      if (att.downloadURL) {
        await downloadWithFileName(att.downloadURL, att.name);
        return;
      }
      if (att.storagePath) {
        await openFileFromPath(att.storagePath, att.name);
      }
    },
    [downloadWithFileName, openFileFromPath],
  );

  const handleCopyReference = useCallback(async () => {
    const referenceId = String(data?.submission?.id || "").trim();
    if (!referenceId) return;

    try {
      await navigator.clipboard.writeText(referenceId);
    } catch (copyError) {
      console.error("Failed to copy reference id:", copyError);
    }
  }, [data?.submission?.id]);

  useEffect(() => {
    if (!submissionId) {
      setLoading(false);
      setError("Missing submission id.");
      return undefined;
    }

    let isCancelled = false;
    let unsubSubmission = () => {};
    let unsubAttempts = () => {};

    const bindSubmission = (resolvedOwnerEmail) => {
      if (isCancelled) return;

      if (!resolvedOwnerEmail) {
        setLoading(false);
        setData(null);
        setError("Missing adviser email for this submission.");
        return;
      }

      setLoading(true);
      setError("");

      const submissionRef = doc(
        dbFinancialPromotions,
        "Approvals",
        resolvedOwnerEmail,
        "Submissions",
        submissionId,
      );

      let hasSubmissionLoaded = false;
      let hasAttemptsLoaded = false;

      const completeLoadingIfReady = () => {
        if (hasSubmissionLoaded && hasAttemptsLoaded && !isCancelled) {
          setLoading(false);
        }
      };

      unsubSubmission = onSnapshot(
        submissionRef,
        (snap) => {
          hasSubmissionLoaded = true;

          if (!snap.exists()) {
            setData(null);
            setError("Submission not found for this id.");
            completeLoadingIfReady();
            return;
          }

          setError("");
          setData((prev) => ({
            submission: {
              id: snap.id,
              ownerEmail: resolvedOwnerEmail,
              ...snap.data(),
            },
            attempts: prev?.attempts || [],
          }));

          completeLoadingIfReady();
        },
        (snapshotError) => {
          hasSubmissionLoaded = true;
          setError(
            snapshotError?.message || "Failed to load submission details.",
          );
          completeLoadingIfReady();
        },
      );

      const attemptsRef = collection(submissionRef, "attempts");
      const attemptsQuery = query(
        attemptsRef,
        orderBy("createdAt", "desc"),
        limit(50),
      );

      unsubAttempts = onSnapshot(
        attemptsQuery,
        (attemptsSnap) => {
          hasAttemptsLoaded = true;

          const attempts = attemptsSnap.docs.map((attemptDoc) => {
            const attemptData = attemptDoc.data() || {};
            const media = Array.isArray(attemptData.media)
              ? attemptData.media
              : [];

            return {
              id: attemptDoc.id,
              ...attemptData,
              media,
              filesMeta: Array.isArray(attemptData?.input?.filesMeta)
                ? attemptData.input.filesMeta
                : [],
            };
          });

          attempts.sort((a, b) => {
            const timeDiff = getAttemptSortTime(b) - getAttemptSortTime(a);
            if (timeDiff !== 0) return timeDiff;

            const attemptNumberDiff =
              Number(b?.attemptNumber || 0) - Number(a?.attemptNumber || 0);
            if (attemptNumberDiff !== 0) return attemptNumberDiff;

            return String(b?.id || "").localeCompare(String(a?.id || ""));
          });

          setData((prev) => ({
            submission: prev?.submission || null,
            attempts,
          }));

          completeLoadingIfReady();
        },
        (snapshotError) => {
          hasAttemptsLoaded = true;
          setError(snapshotError?.message || "Failed to load attempts.");
          completeLoadingIfReady();
        },
      );
    };

    const resolveOwnerEmail = async () => {
      const fallbackOwnerEmail = String(userEmail || "").trim();
      if (!isAdminView && !fallbackOwnerEmail) {
        setLoading(false);
        setError("Missing user email or submission id.");
        return;
      }

      if (ownerEmailFromRoute) {
        bindSubmission(ownerEmailFromRoute);
        return;
      }

      if (!isAdminView) {
        bindSubmission(fallbackOwnerEmail);
        return;
      }

      try {
        const ownerLookupQuery = query(
          collectionGroup(dbFinancialPromotions, "Submissions"),
          where(documentId(), "==", submissionId),
          limit(1),
        );
        const ownerLookupSnapshot = await getDocs(ownerLookupQuery);
        const ownerDoc = ownerLookupSnapshot.docs[0];
        const resolvedOwnerEmail =
          ownerDoc?.ref.parent?.parent?.id ||
          String(ownerDoc?.data()?.createdByEmail || "").trim();

        if (!resolvedOwnerEmail) {
          if (!isCancelled) {
            setLoading(false);
            setData(null);
            setError("Submission not found for this id.");
          }
          return;
        }

        bindSubmission(resolvedOwnerEmail);
      } catch (lookupError) {
        if (!isCancelled) {
          setLoading(false);
          setError(
            lookupError?.message || "Failed to resolve submission owner.",
          );
        }
      }
    };

    resolveOwnerEmail();

    return () => {
      isCancelled = true;
      unsubSubmission();
      unsubAttempts();
    };
  }, [isAdminView, ownerEmailFromRoute, submissionId, userEmail]);

  // console.log("Current submission data:", data);

  const handleResubmit = useCallback(
    async (latestAttempt) => {
      setResubmitError("");
      setResubmitSuccess("");

      const cleanedText = String(resubmitText || "").trim();
      const cleanedUrl = String(resubmitUrl || "").trim();

      if (!cleanedUrl && !cleanedText && resubmitFiles.length === 0) {
        setResubmitError(
          "Please provide a URL, upload at least one file, or paste some text.",
        );
        return;
      }

      if (cleanedUrl && !looksLikeUrl(cleanedUrl)) {
        setResubmitError(
          "Please enter a valid URL starting with http:// or https://",
        );
        return;
      }

      if (cleanedText && cleanedText.length > MAX_TEXT_CHARS) {
        setResubmitError(
          `Text is too long. Max ${MAX_TEXT_CHARS.toLocaleString()} characters.`,
        );
        return;
      }

      if (resubmitFiles.length > MAX_FILES) {
        setResubmitError(`Too many files. Max ${MAX_FILES}.`);
        return;
      }

      setResubmitLoading(true);
      // console.log("Resubmitting with data:", {
      //   userEmail: data.submission.ownerEmail,
      //   submissionId: data?.submission?.id || submissionId,
      //   reuploadOfAttemptId: latestAttempt?.id || null,
      // });

      try {
        const payload = {
          userEmail: data.submission.ownerEmail,
          submissionId: data?.submission?.id || submissionId,
          reuploadOfAttemptId: latestAttempt?.id || null,

          text: cleanedText,
          url: cleanedUrl,
          notes: resubmitMessage || "",
          title: latestAttempt?.input?.title || data?.submission?.title || "",
          mediaTypes: Array.isArray(latestAttempt?.input?.mediaTypes)
            ? latestAttempt.input.mediaTypes
            : [],
          productTypes: Array.isArray(latestAttempt?.input?.productTypes)
            ? latestAttempt.input.productTypes
            : [],
          entityType: latestAttempt?.input?.entityType || "unknown",
          adviserMeta: latestAttempt?.input?.adviserMeta || null,
          userDataMeta: latestAttempt?.input?.userDataMeta || null,
        };

        await submitFpForReview({
          backendUrl: BACKEND_URL,
          payload,
          files: resubmitFiles,
        });

        setResubmitSuccess(
          "Resubmitted successfully. Our compliance team will review it.",
        );
        setResubmitText("");
        setResubmitUrl("");
        setResubmitFiles([]);
        setResubmitMessage("");
      } catch (submitError) {
        setResubmitError(submitError?.message || "Something went wrong");
      } finally {
        setResubmitLoading(false);
      }
    },
    [
      data?.submission?.id,
      data?.submission?.title,
      resubmitFiles,
      resubmitMessage,
      resubmitText,
      resubmitUrl,
      submissionId,
      userEmail,
    ],
  );

  const latestApprovedAttempt = useMemo(() => {
    const attempts = Array.isArray(data?.attempts) ? data.attempts : [];
    return (
      attempts.find(
        (attempt) =>
          isApprovedLike(attempt?.decision) ||
          isApprovedLike(attempt?.review?.decision) ||
          isApprovedLike(attempt?.status),
      ) || null
    );
  }, [data?.attempts]);

  const approvalDateValue =
    data?.submission?.approvedAt ||
    latestApprovedAttempt?.reviewedAt ||
    latestApprovedAttempt?.review?.reviewedAt ||
    latestApprovedAttempt?.updatedAt ||
    latestApprovedAttempt?.completedAt ||
    null;

  const expiryDateValue =
    data?.submission?.expiredAt ||
    (approvalDateValue ? addOneYear(approvalDateValue) : null);

  const approvalConditions = useMemo(() => {
    const source =
      latestApprovedAttempt?.approvalConditions ||
      latestApprovedAttempt?.review?.approvalConditions ||
      [];

    if (!Array.isArray(source)) return [];
    return source
      .map((item) =>
        String(item || "")
          .replace(/^\s*[•\-*]\s*/, "")
          .trim(),
      )
      .filter(Boolean);
  }, [latestApprovedAttempt]);

  const isSubmissionApproved = useMemo(
    () => isApprovedLike(data?.submission?.status),
    [data?.submission?.status],
  );

  const approvedMediaAcrossAttempts = useMemo(() => {
    if (!isSubmissionApproved) return [];

    const attempts = Array.isArray(data?.attempts) ? data.attempts : [];
    const approved = [];
    const seen = new Set();

    for (const attempt of attempts) {
      const fileReviewDecisions =
        attempt?.fileReviewDecisions &&
        typeof attempt.fileReviewDecisions === "object"
          ? attempt.fileReviewDecisions
          : {};
      const decisionByFile = buildDecisionByFile(attempt);
      const attachments = buildAttemptAttachments(attempt);

      for (const att of attachments) {
        const savedFileDecision =
          fileReviewDecisions[getAttachmentDecisionKey(att)]?.decision ||
          fileReviewDecisions[normalizeName(att.name)]?.decision;

        const decision = savedFileDecision
          ? toDisplayDecision(savedFileDecision)
          : decisionByFile.get(normalizeItemKey(att.itemKey)) ||
            decisionByFile.get(normalizeName(att.name)) ||
            "Pending";

        if (decision !== "Approved") continue;

        const uniqueKey = `${normalizeName(att.name)}|${String(att.storagePath || att.downloadURL || "")}`;
        if (seen.has(uniqueKey)) continue;
        seen.add(uniqueKey);

        approved.push({
          ...att,
          decision,
          attemptId: attempt?.id || "",
          attemptNumber: attempt?.attemptNumber || "",
          attemptUpdatedAt:
            attempt?.updatedAt || attempt?.completedAt || attempt?.createdAt,
        });
      }
    }

    return approved;
  }, [data?.attempts, isSubmissionApproved]);

  const breadcrumbItems = [
    { label: "Marketing & Promotions", to: "/portal/marketing-promotions" },
    { label: "Financial Promotions", to: "/financial-promotions" },
    { label: "Promotion Details" },
  ];

  return (
    <div className="fp-container">
      <Sidebar />
      <div className="fp-details-page">
        <Breadcrumb items={breadcrumbItems} />

        <div className="fp-dashboard-header fp-details-header">
          <div className="fp-header">
            <BackIcon onClick={() => navigate("/financial-promotions")} />
            <div className="FP_H3">Promotion Details</div>
          </div>
          <StatusTag status={getDecision(data?.submission?.status)} />
        </div>

        {loading && <div className="FP_Help_Text">Loading details...</div>}
        {!loading && error && <div className="FP_Help_Text">{error}</div>}

        {!loading && !error && data && (
          <>
            <section className="fp-details-section">
              <div className="FP_Subtitle">Overview</div>

              <div className="FP_Content">
                {Array.isArray(data?.attempts?.[0]?.input?.mediaTypes)
                  ? data.attempts[0].input.mediaTypes.join(", ")
                  : ""}
              </div>

              <div className="fp-details-item">
                <div className="FP_Body_Semibold">
                  {data.submission?.title || ""}
                </div>
              </div>

              <div className="fp-details-grid">
                <div className="fp-details-item">
                  <div className="fp-details-label FP_Content">Reference</div>
                  <div className="fp-details-value FP_Content">
                    <button
                      type="button"
                      className="fp-copy-reference-btn"
                      onClick={handleCopyReference}
                      title="Copy reference"
                      aria-label="Copy reference"
                    >
                      <span className="FP_Content">
                        {data.submission?.referenceNumber ||
                          `${data.submission?.id}`}
                      </span>
                      <CopyIcon />
                    </button>
                  </div>
                </div>

                <div className="fp-details-item">
                  <div className="fp-details-label FP_Content">Submitted</div>
                  <div className="fp-details-value FP_Content">
                    {formatDate(data.submission?.createdAt)}
                  </div>
                </div>

                {getDecision(data?.submission?.status) === "Approved" && (
                  <div className="fp-details-item">
                    <div className="fp-details-label FP_Content">Approved</div>
                    <div className="fp-details-value FP_Content">
                      {formatDate(approvalDateValue)}
                    </div>
                  </div>
                )}

                {getDecision(data?.submission?.status) === "Approved" &&
                  expiryDateValue && (
                    <div className="fp-details-item">
                      <div className="fp-details-label FP_Content">Expired</div>
                      <div className="fp-details-value FP_Content">
                        {formatDate(expiryDateValue)}
                      </div>
                    </div>
                  )}
              </div>

              {approvalConditions.length > 0 ? (
                <>
                  <div className="FP_Content_Semibold">
                    Approval Conditions:
                  </div>
                  <ul className="fp-details-list FP_Content">
                    {approvalConditions.map((condition, index) => (
                      <li key={`approval-condition-${index}`}>{condition}</li>
                    ))}
                  </ul>
                </>
              ) : (
                // <div className="FP_Content">
                //   No approval conditions available.
                // </div>
                <></>
              )}

              <div className="FP_Content_Semibold">Please Note:</div>
              <ul className="fp-details-list FP_Content">
                <li>
                  This financial promotion has been checked for regulatory
                  compliance, we cannot guarantee accuracy of spelling and/or
                  punctuation.
                </li>
              </ul>
            </section>

            {isSubmissionApproved &&
              approvedMediaAcrossAttempts.length !== 0 && (
                <section className="fp-details-section">
                  <div className="FP_Subtitle">Approved Media</div>
                  {approvedMediaAcrossAttempts.length === 0 ? (
                    <div className="FP_Help_Text">
                      No approved media items found across attempts.
                    </div>
                  ) : (
                    <div className="fp-attachment-list">
                      {approvedMediaAcrossAttempts.map((att, index) => {
                        const canOpen = !!(att.downloadURL || att.storagePath);
                        const showApprovedIcon =
                          isSubmissionApproved && att?.decision === "Approved";
                        return (
                          <div
                            key={`${att.name}-${att.attemptId}-${index}`}
                            className={`fp-attachment-row ${canOpen ? "" : "is-disabled"}`}
                          >
                            <div className="fp-attachment-row-main">
                              <div className="fp-attachment-info">
                                <button
                                  type="button"
                                  className="fp-attachment-name-pill FP_Content"
                                  onClick={
                                    canOpen
                                      ? () => openAttachment(att)
                                      : undefined
                                  }
                                  disabled={!canOpen}
                                  title={att.name}
                                >
                                  <span className="fp-attachment-name-text">
                                    {att.name}
                                  </span>
                                  {showApprovedIcon && (
                                    <CheckCircleIcon
                                      className="fp-attachment-pill-icon"
                                      aria-hidden="true"
                                    />
                                  )}
                                </button>
                                {/* <div className="fp-attachment-meta FP_Content">
                                {`Attempt ${att.attemptNumber || "-"} • ${formatDateTime(att.attemptUpdatedAt)}`}
                              </div> */}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
              )}

            <section className="fp-details-section fp-details-section--attempts">
              <div className="fp-attempt-list">
                {(data.attempts || []).map((attempt, index) => {
                  const isLatestAttempt = index === 0;
                  const isActionRequired = [
                    "action_required",
                    "requested_changes",
                    "changes_requested",
                  ].includes(
                    String(data?.submission?.status || "")
                      .trim()
                      .toLowerCase(),
                  );
                  const reuploadOfAttemptId =
                    attempt?.reuploadOfAttemptId || "";
                  const messageText = attempt?.input?.notes || "";
                  const staffReviewText =
                    attempt?.review?.staffReviewText ||
                    attempt?.review?.reviewerNotes ||
                    attempt?.review?.feedback ||
                    attempt?.staffReviewText ||
                    attempt?.feedback ||
                    "";
                  const textContent = attempt?.input?.text || "";
                  const submittedUrl = String(attempt?.input?.url || "").trim();
                  const submittedHref = (() => {
                    if (!submittedUrl) return "";

                    const candidate = looksLikeUrl(submittedUrl)
                      ? submittedUrl
                      : `https://${submittedUrl}`;

                    try {
                      return new URL(candidate).href;
                    } catch {
                      return "";
                    }
                  })();
                  const formattedStaffReviewText =
                    formatMultilineText(staffReviewText);
                  const content = textContent || "No content available.";
                  const attachments = buildAttemptAttachments(attempt);
                  const fileReviewDecisions =
                    attempt?.fileReviewDecisions &&
                    typeof attempt.fileReviewDecisions === "object"
                      ? attempt.fileReviewDecisions
                      : {};
                  const decisionByFile = buildDecisionByFile(attempt);

                  return (
                    <article
                      className="fp-attempt-card fp-attempt-card--submission"
                      key={attempt.id}
                    >
                      {/* <div className="FP_Subtitle fp-attempt-submission-title">
                        Content Submitted
                      </div> */}
                      <div className="fp-attempt-submission-header">
                        <div className="FP_Subtitle fp-attempt-submission-title">
                          {!reuploadOfAttemptId
                            ? "Original Submission"
                            : `Attempt ${attempt.attemptNumber || ""}`}
                        </div>
                        <div className="FP_Content fp-attempt-submission-date">
                          {formatDateTime(
                            attempt.updatedAt ||
                              attempt.completedAt ||
                              attempt.createdAt,
                          )}
                        </div>
                      </div>

                      <div className="fp-attempt-intro FP_Content">
                        <span className="FP_Content_Semibold">Message: </span>
                        {messageText || "No message provided."}
                      </div>

                      <span className="FP_Content_Semibold">
                        Staff Review:{" "}
                      </span>
                      <div className="fp-attempt-intro FP_Content fp-attempt-content-panel">
                        <span className="fp-attempt-intro-text ">
                          {formattedStaffReviewText ||
                            "No staff review provided."}
                        </span>
                      </div>

                      <span className="FP_Content_Semibold">
                        Submitted Content:{" "}
                      </span>
                      <div className="fp-attempt-content-panel">
                        <div className="fp-attempt-content FP_Content">
                          {content}
                          {submittedUrl && (
                            <div className="fp-attempt-content-url">
                              <span className="FP_Content_Semibold">URL: </span>
                              {submittedHref ? (
                                <a
                                  className="fp-attempt-url-link"
                                  href={submittedHref}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {submittedUrl}
                                </a>
                              ) : (
                                submittedUrl
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {attachments.length !== 0 && (
                        <div className="fp-attempt-footer">
                          <div className="fp-attempt-footer-label FP_Content_Italic FP_Content">
                            Attachments
                          </div>

                          {attachments.length === 0 ? (
                            <div className="FP_Help_Text">No attachments.</div>
                          ) : (
                            <div className="fp-attachment-list">
                              {attachments.map((att, index) => {
                                const canOpen = !!(
                                  att.downloadURL || att.storagePath
                                );
                                const savedFileDecision =
                                  fileReviewDecisions[
                                    getAttachmentDecisionKey(att)
                                  ]?.decision ||
                                  fileReviewDecisions[normalizeName(att.name)]
                                    ?.decision;

                                const attachmentDecision = savedFileDecision
                                  ? toDisplayDecision(savedFileDecision)
                                  : decisionByFile.get(
                                      normalizeItemKey(att.itemKey),
                                    ) ||
                                    decisionByFile.get(
                                      normalizeName(att.name),
                                    ) ||
                                    "Pending";

                                const showApprovedIcon =
                                  isSubmissionApproved &&
                                  attachmentDecision === "Approved";
                                return (
                                  <div
                                    key={`${att.name}-${index}`}
                                    className={`fp-attachment-row ${canOpen ? "" : "is-disabled"}`}
                                  >
                                    <div className="fp-attachment-row-main">
                                      <div className="fp-attachment-info">
                                        <button
                                          type="button"
                                          className="fp-attachment-name-pill FP_Content"
                                          onClick={
                                            canOpen
                                              ? () => openAttachment(att)
                                              : undefined
                                          }
                                          disabled={!canOpen}
                                          title={att.name}
                                        >
                                          <span className="fp-attachment-name-text">
                                            {att.name}
                                          </span>
                                          {showApprovedIcon && (
                                            <CheckCircleIcon
                                              className="fp-attachment-pill-icon"
                                              aria-hidden="true"
                                            />
                                          )}
                                        </button>
                                        {/* <div className="fp-attachment-meta FP_Content">
                                          {att.mimeType || "—"}
                                          {typeof att.size === "number"
                                            ? ` • ${Math.round(att.size / 1024)} KB`
                                            : ""}
                                        </div> */}
                                      </div>
                                    </div>

                                    {/* <button
                                      type="button"
                                      className="fp-new-submission-btn fp-btn-secondary"
                                      onClick={() =>
                                        canOpen && openAttachment(att)
                                      }
                                      disabled={!canOpen}
                                      title={
                                        canOpen
                                          ? "Open file"
                                          : "No file path available"
                                      }
                                    >
                                      Open file
                                    </button> */}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}

                      {isLatestAttempt && isActionRequired && (
                        <div
                          className="fp-form-column"
                          style={{ marginTop: 16 }}
                        >
                          <div className="FP_Subtitle">Resubmit Content</div>

                          <div
                            className="FP_Content"
                            style={{ marginBottom: 8 }}
                          >
                            Add updated content and/or upload updated files for
                            re-review.
                          </div>

                          <textarea
                            value={resubmitText}
                            onChange={(event) =>
                              setResubmitText(event.target.value)
                            }
                            placeholder="Enter your updated content here..."
                            className="fp-form-input fp-form-textarea FP_Content"
                            disabled={resubmitLoading}
                          />

                          <input
                            type="text"
                            value={resubmitUrl}
                            onChange={(event) =>
                              setResubmitUrl(event.target.value)
                            }
                            placeholder="URL Link"
                            className="fp-form-input FP_Content"
                            disabled={resubmitLoading}
                          />

                          <textarea
                            value={resubmitMessage}
                            onChange={(event) =>
                              setResubmitMessage(event.target.value)
                            }
                            placeholder="Enter any message/comments for resubmission"
                            className="FP_Content fp-form-input fp-form-message"
                            disabled={resubmitLoading}
                          />

                          <FileUpload
                            label={null}
                            description="Drag & drop files here or"
                            browseText="Browse"
                            status="default"
                            disabled={resubmitLoading}
                            accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.webp,.mp4,.mov,.avi,.mp3,.wav,.m4a"
                            maxFileSize={MAX_FILE_SIZE}
                            multiple
                            files={resubmitFiles}
                            onFilesChange={setResubmitFiles}
                          />

                          {resubmitError && (
                            <div className="fp-form-error">{resubmitError}</div>
                          )}
                          {resubmitSuccess && (
                            <div className="fp-form-success">
                              {resubmitSuccess}
                            </div>
                          )}

                          <div
                            className="fp-form-actions"
                            style={{ marginTop: 12 }}
                          >
                            <FPPrimaryButton
                              type="button"
                              loading={resubmitLoading}
                              label="Resubmit"
                              onClick={() => handleResubmit(attempt)}
                            />
                          </div>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            </section>

            {/* <section className="fp-details-section">
              <div className="FP_H4">Raw JSON</div>
              <pre className="fp-details-json">
                {JSON.stringify(data, null, 2)}
              </pre>
            </section> */}
          </>
        )}
      </div>
    </div>
  );
}

export default FPDetails;
